import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';

import { PmAdvancedApiService } from '../pm-advanced/pm-advanced.service';
import { SharedRoadmapDto } from '../pm-advanced/pm-advanced.models';

@Component({
  selector: 'app-shared-roadmap',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, MatIconModule],
  template: `
    <section class="shared-roadmap">
      <header>
        <h1>{{ data()?.projectName || 'Shared roadmap' }}</h1>
        @if (data()?.zoom) { <p class="muted">View: {{ data()!.zoom }}</p> }
      </header>
      @if (loading()) { <mat-spinner diameter="36"></mat-spinner> }
      @if (error()) { <p class="error">{{ error() }}</p> }
      @if (data(); as d) {
        @if (d.rangeStart && d.rangeEnd) {
          <p class="range">{{ d.rangeStart | date:'mediumDate' }} – {{ d.rangeEnd | date:'mediumDate' }}</p>
        }
        @for (lane of lanes(d); track lane) {
          <div class="lane">
            <h3>{{ lane }}</h3>
            @for (item of laneItems(d, lane); track item.id) {
              <div class="item">
                <strong>{{ item.title }}</strong>
                <span>{{ item.startDate | date:'mediumDate' }} – {{ item.endDate | date:'mediumDate' }}</span>
                @if (item.progressPercent != null) { <span>{{ item.progressPercent }}%</span> }
              </div>
            }
          </div>
        }
        @if (d.dependencies?.length) {
          <h3>Dependencies</h3>
          <ul>
            @for (dep of d.dependencies!; track (dep.fromItemId || dep.fromId || '') + (dep.toItemId || dep.toId || '')) {
              <li>{{ depLabel(d, dep.fromItemId || dep.fromId || '') }} → {{ depLabel(d, dep.toItemId || dep.toId || '') }}</li>
            }
          </ul>
        }
      }
    </section>
  `,
  styles: [`
    .shared-roadmap { max-width: 960px; margin: 32px auto; padding: 0 24px; }
    h1 { margin: 0 0 8px; }
    .muted, .range { color: #64748b; }
    .error { color: #b91c1c; background: #fef2f2; padding: 10px 12px; border-radius: 8px; }
    .lane { margin: 16px 0; }
    .item { display: flex; flex-wrap: wrap; gap: 12px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; font-size: 0.9rem; }
  `]
})
export class SharedRoadmapComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly pm = inject(PmAdvancedApiService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly data = signal<SharedRoadmapDto | null>(null);

  ngOnInit(): void {
    const token = this.route.snapshot.paramMap.get('token') ?? '';
    if (!token) {
      this.error.set('Invalid share link.');
      this.loading.set(false);
      return;
    }
    this.pm.getSharedRoadmap(token).subscribe({
      next: (d) => {
        const bars = Array.isArray((d as any)?.bars) ? (d as any).bars : [];
        if (bars.length && !(d.items?.length)) {
          d = {
            ...d,
            items: bars
              .filter((b: any) => b.startDate || b.endDate)
              .map((b: any) => ({
                id: b.id,
                lane: b.itemType || 'Other',
                title: b.title,
                startDate: b.startDate || b.endDate,
                endDate: b.endDate || b.startDate,
                itemType: b.itemType,
                status: b.status
              })),
            rangeStart: d.rangeStart || (d as any).from,
            rangeEnd: d.rangeEnd || (d as any).to,
            dependencies: (d.dependencies ?? []).map((dep: any) => ({
              ...dep,
              fromItemId: dep.fromItemId || dep.fromId,
              toItemId: dep.toItemId || dep.toId
            }))
          };
        }
        this.data.set(d);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('This shared roadmap is unavailable or has expired.');
        this.loading.set(false);
      }
    });
  }

  lanes(d: SharedRoadmapDto): string[] {
    const order = ['Epics', 'Sprints', 'Versions', 'Milestones'];
    const found = [...new Set((d.items ?? []).map(i => i.lane || 'Other'))];
    return [...order.filter(l => found.includes(l)), ...found.filter(l => !order.includes(l))];
  }

  laneItems(d: SharedRoadmapDto, lane: string) {
    return (d.items ?? []).filter(i => (i.lane || 'Other') === lane);
  }

  depLabel(d: SharedRoadmapDto, id: string): string {
    return d.items?.find(i => i.id === id)?.title ?? id;
  }
}
